
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check, ChevronsUpDown, KeyRound, LogOut, Monitor, Moon, Sun } from "lucide-react";
import { useTranslation } from "react-i18next";

import { setAdminLocale } from "../../i18n-provider";
import { useRouter } from "../router";
import { initialsOf } from "./initials";
import { setThemePreference, useTheme, type ThemePreference } from "./preferences";
import styles from "./shell.module.css";
import { useFocusHandoff } from "./use-focus-handoff";

export type ShellUser = { name: string; email: string; role: string };

type Props = {
  user: ShellUser | null;
  onSignOut: () => void;
  compact?: boolean;
  side?: "top" | "bottom" | "right";
};

const ICON_PROPS = { className: styles.menuItemIcon, strokeWidth: 1.75, "aria-hidden": true } as const;

export function UserMenu({ user, onSignOut, compact = false, side = "top" }: Props) {
  const { t, i18n } = useTranslation();
  const { preference } = useTheme();
  const router = useRouter();
  const focusHandoff = useFocusHandoff();

  if (!user) {
    return (
      <span className={`${styles.userButton} ${compact ? styles.userButtonCompact : ""}`} aria-hidden="true">
        <span className={`${styles.avatar} ${styles.avatarPlaceholder}`} />
      </span>
    );
  }

  const themes: { value: ThemePreference; label: string; icon: typeof Sun }[] = [
    { value: "light", label: t("nav.shell.themeLight"), icon: Sun },
    { value: "dark", label: t("nav.shell.themeDark"), icon: Moon },
    { value: "system", label: t("nav.shell.themeSystem"), icon: Monitor },
  ];
  const locales = [
    { value: "pt-BR", label: t("nav.shell.languagePortuguese") },
    { value: "en-US", label: t("nav.shell.languageEnglish") },
  ];

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className={`${styles.userButton} ${compact ? styles.userButtonCompact : ""}`}
          aria-label={t("nav.shell.accountMenu", { name: user.name })}
        >
          <span className={styles.avatar} aria-hidden="true">{initialsOf(user.name)}</span>
          {!compact && (
            <>
              <span className={styles.userName}>{user.name}</span>
              <ChevronsUpDown className={styles.userChevron} strokeWidth={1.75} aria-hidden="true" />
            </>
          )}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={styles.menu}
          side={side}
          align={compact ? "end" : "start"}
          sideOffset={6}
          collisionPadding={8}
          onCloseAutoFocus={focusHandoff.onCloseAutoFocus}
        >
          <div className={styles.menuHeader}>
            <span className={styles.menuHeaderName}>{user.name}</span>
            <span className={styles.menuHeaderMeta}>
              {t("nav.shell.role", { role: t(`operators.roles.${user.role}`, { defaultValue: user.role }) })}
            </span>
          </div>
          <DropdownMenu.Separator className={styles.menuSeparator} />
          <DropdownMenu.Label className={styles.menuLabel}>{t("nav.shell.theme")}</DropdownMenu.Label>
          <DropdownMenu.RadioGroup value={preference} onValueChange={(value) => setThemePreference(value as ThemePreference)}>
            {themes.map(({ value, label, icon: Icon }) => (
              <DropdownMenu.RadioItem key={value} value={value} className={styles.menuItem}>
                <Icon {...ICON_PROPS} />
                {label}
                <DropdownMenu.ItemIndicator className={styles.menuCheck}>
                  <Check {...ICON_PROPS} />
                </DropdownMenu.ItemIndicator>
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
          <DropdownMenu.Separator className={styles.menuSeparator} />
          <DropdownMenu.Label className={styles.menuLabel}>{t("nav.shell.language")}</DropdownMenu.Label>
          <DropdownMenu.RadioGroup value={i18n.language} onValueChange={setAdminLocale}>
            {locales.map(({ value, label }) => (
              <DropdownMenu.RadioItem key={value} value={value} className={styles.menuItem} lang={value}>
                <span className={styles.menuItemCode} aria-hidden="true">{value.slice(0, 2).toUpperCase()}</span>
                {label}
                <DropdownMenu.ItemIndicator className={styles.menuCheck}>
                  <Check {...ICON_PROPS} />
                </DropdownMenu.ItemIndicator>
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
          <DropdownMenu.Separator className={styles.menuSeparator} />
          <DropdownMenu.Item
            className={styles.menuItem}
            onSelect={() => {
              focusHandoff.handOff();
              router.push("/profile");
            }}
          >
            <KeyRound {...ICON_PROPS} />
            {t("nav.shell.security")}
          </DropdownMenu.Item>
          <DropdownMenu.Item className={`${styles.menuItem} ${styles.menuItemDanger}`} onSelect={onSignOut}>
            <LogOut {...ICON_PROPS} />
            {t("nav.shell.signOut")}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
